import { BASE_URLS } from '../../../config/base-urls';
import { createApi } from '../../../config/create-api';
import { formatOrderParam, makeError } from '../../../config/research/v1';
import { formatV2SearchParams } from '../../../config/research/v2';
import { getStudioHeaders } from '../config/headers';

import { mapVisualizationV2ToV1 } from './utils/version-mappers';
import { prepareVisulationFilters } from './utils/visualizations-api';
import {
    DeleteVisualizationRequest,
    DeleteVisualizationResponse,
    GetVisualizationRequestV2,
    GetVisualizationResponse,
    GetVisualizationResponseV2,
    GetVisualizationsRequest,
    GetVisualizationsRequestV2,
    GetVisualizationsResponse,
    GetVisualizationsResponseV2,
    PatchUpdateVisualizationRequest,
    PostCreateVisualizationRequest,
    PostCreateVisualizationResponse,
    STUDIO_VISUALIZATIONS_ENDPOINTS,
    STUDIO_VISUALIZATIONS_V2_ENDPOINTS,
} from './models';
import { initVizConfig } from './utils';

const studioVisualizationsApi = createApi(build => ({
    get: {
        /**
         * @todo migrate mapping to proper v2 request/response
         **/
        visualization: build<GetVisualizationResponse, GetVisualizationRequestV2>({
            cachePrefix: 'visualization-by-id',
            queryFn: async ({ id, expand, sameOriginProxy }, { baseFetch }) => {
                try {
                    const { baseUrl, path } = sameOriginProxy
                        ? {
                              baseUrl: BASE_URLS['same-origin'],
                              path: STUDIO_VISUALIZATIONS_ENDPOINTS.GET.VISUALIZATION_PROXY,
                          }
                        : {
                              baseUrl: BASE_URLS['research-api-v2'],
                              path: STUDIO_VISUALIZATIONS_V2_ENDPOINTS.GET.VISUALIZATION,
                          };

                    const vizResponse = await baseFetch<false, any, GetVisualizationResponseV2>({
                        url: `${baseUrl}${path({ id })}${formatV2SearchParams({
                            expand: expand ?? ['user', 'tags', 'query', 'query.last_execution_cache'],
                        })}`,
                    });

                    if (sameOriginProxy) return vizResponse as any;

                    const mappedResponse = mapVisualizationV2ToV1(vizResponse);
                    if (!mappedResponse || !mappedResponse?.data) {
                        return makeError('Visualization not found.');
                    }

                    return mappedResponse;
                } catch (error) {
                    const err = error as Error;
                    console.error(err);

                    return makeError('Visualization not found.');
                }
            },
        }),
        visualizationsV2: build<GetVisualizationsResponseV2, GetVisualizationsRequestV2>({
            cachePrefix: 'visualizations-v2',
            baseUrl: 'research-api-v2',
            query: ({ order_by, order_dir, expand, count = 100, filters } = {}) => {
                return {
                    uri: STUDIO_VISUALIZATIONS_V2_ENDPOINTS.GET.VISUALIZATIONS(),
                    params: {
                        expand,
                        order_dir,
                        order_by,
                        count,
                        query: prepareVisulationFilters(filters) as any,
                    },
                };
            },
        }),
        /** @deprecated migrate to visualizationsV2 endpoint */
        visualizations: build<GetVisualizationsResponse, GetVisualizationsRequest>({
            cachePrefix: 'all-visualizations',
            query: ({ userIds, types, queryIds, tagIds, order, filter, limit = 200 }) => {
                const uri =
                    typeof queryIds === 'string'
                        ? STUDIO_VISUALIZATIONS_ENDPOINTS.GET.VISUALIZATIONS_BY_QUERY({ queryId: queryIds })
                        : STUDIO_VISUALIZATIONS_ENDPOINTS.GET.VISUALIZATIONS();
                return {
                    uri,
                    options: {
                        headers: getStudioHeaders(),
                    },
                    params: {
                        ids: queryIds,
                        userIds,
                        types,
                        order: formatOrderParam(order),
                        filter,
                        tagIds,
                        /** need to eventually add pagination */
                        limit,
                    },
                };
            },
        }),
    },
    patch: {
        /** @todo migrate to v2 */
        updateVisualization: build<any, PatchUpdateVisualizationRequest>({
            cachePrefix: 'update-visualization',
            query: ({ id, ...body }) => {
                const maybeUpdateConfig = body.config ? { config: JSON.stringify(body.config) } : {};

                return {
                    uri: STUDIO_VISUALIZATIONS_ENDPOINTS.PATCH.VISUALIZATION({ id }),
                    options: {
                        headers: getStudioHeaders(),
                        method: build.Method.Patch,
                        body: {
                            ...body,
                            ...maybeUpdateConfig,
                        },
                    },
                };
            },
        }),
    },
    post: {
        /** @todo migrate to v2 */
        createVisualization: build<PostCreateVisualizationResponse, PostCreateVisualizationRequest>({
            cachePrefix: 'create-visualization',
            queryFn: async (
                { title, subTitle, type, config, tagIds, queryId, lastSuccessfulExecution, userId },
                { baseFetch },
            ) => {
                try {
                    const createResponse = await baseFetch<false, any, PostCreateVisualizationResponse>({
                        url: `${BASE_URLS['research-api']}${STUDIO_VISUALIZATIONS_ENDPOINTS.POST.VISUALIZATION()}`,
                        method: build.Method.Post,
                        headers: getStudioHeaders(),
                        body: {
                            title,
                            subTitle,
                            type,
                            queryId,
                            config: config ? JSON.stringify(config) : initVizConfig({ type, lastSuccessfulExecution }),
                            userId,
                            tagIds,
                        },
                    });

                    return createResponse;
                } catch (error) {
                    const err = error as Error;
                    console.error(err);

                    return makeError('Failed to create visualization');
                }
            },
        }),
    },
    delete: {
        /** @todo migrate to v2 */
        visualization: build<DeleteVisualizationResponse, DeleteVisualizationRequest>({
            cachePrefix: 'delete-visualization',
            query: ({ id }) => {
                return {
                    uri: STUDIO_VISUALIZATIONS_ENDPOINTS.DELETE.VISUALIZATION({ id }),
                    options: {
                        headers: getStudioHeaders(),
                        method: build.Method.Delete,
                    },
                };
            },
        }),
    },
}));

export { studioVisualizationsApi };
